import { Button, styled } from '@mui/material';
import { useStoreActions, useStoreState } from 'stores/global';
import { useEffect, useMemo } from 'react';
import { TaskBarModalProps } from 'modules/modalManager/types';
import useLocalStorageState from 'use-local-storage-state';
import { Modal } from './Modal';
import { clearDeferredModalContent, getDeferredModalContent } from '../hooks';

export const TaskBarModal = (props: TaskBarModalProps) => {
  const {
    minimize = true,
    id,
    trigger,
    onClose,
    onMinimize,
    onOpen,
    onBackdropClick,
    resolve,
    showAgainCheckbox,
    description,
    title,
    taskBarTitle,
    ...rest
  } = props as TaskBarModalProps;

  const { activeId } = useStoreState(state => state.modalManager);
  const { add, remove, setActiveId, setModalListVisibility } = useStoreActions(
    actions => actions.modalManager
  );

  const deferred = getDeferredModalContent(id);

  const [dontShowAgain, setShowAgain] = useLocalStorageState(`${showAgainCheckbox}-showAgain`, {
    defaultValue: false,
  });

  const styles = useMemo(() => {
    return {
      zIndex: activeId === id ? 1300 : -1,
    };
  }, [activeId]);

  const handleOpen = () => {
    setActiveId(id);
    add({ ...props });
    onOpen && onOpen();
  };

  const handleClose: TaskBarModalProps['onClose'] = (event, reason) => {
    setActiveId('');
    remove(id);
    clearDeferredModalContent(id);
    onClose && onClose(event, reason);
    resolve && resolve(reason);

    if (reason === 'backdropClick') {
      onBackdropClick && onBackdropClick();
    }
  };

  const handleMinimize = () => {
    setActiveId('');
    setModalListVisibility(true);
    onMinimize && onMinimize();
  };

  useEffect(() => {
    return () => {
      clearDeferredModalContent(id);
    };
  }, [id]);

  return (
    <>
      <div>
        {trigger?.Component ? (
          <CustomButton onClick={handleOpen}>{trigger?.Component}</CustomButton>
        ) : (
          <Button
            onClick={handleOpen}
            variant={trigger?.variant ? trigger.variant : 'secondary'}
            disableRipple
          >
            {trigger?.label}
          </Button>
        )}
      </div>
      <Modal
        {...(rest as TaskBarModalProps)}
        id={id}
        minimize={minimize}
        data-testid="modal_component"
        open={Boolean(activeId === id)}
        keepMounted={minimize}
        handleMinimize={handleMinimize}
        sx={styles}
        onClose={(event, reason) => handleClose(event, reason)}
        onActionsClick={(event, reason) => {
          handleClose(event ?? {}, reason ?? '');
        }}
        slotProps={{
          backdrop: { sx: { backgroundColor: 'transparent', backdropFilter: 'blur(2px)' } },
        }}
        title={title ?? taskBarTitle}
        description={description}
        contents={props.contents ?? deferred?.contents}
        dontShowAgain={dontShowAgain}
        onDontShowAgainChange={() => {
          setShowAgain(prev => !prev);
        }}
      >
        {props.children ?? deferred?.children}
      </Modal>
    </>
  );
};

const CustomButton = styled('button')({
  margin: 0,
  padding: 0,
  border: 0,
  background: 'transparent',
  display: 'flex',
  cursor: 'pointer',
});
